import { setProduct } from "./productAction";

// Catalogue global des produits canoniques, et adoption d'un de ces produits
// par le compte connecté.
//
// Les deux appels passent par les relais Express /stocks/catalogue et
// /stocks/catalogue/adopt : le navigateur ne parle jamais au storehouse
// directement, et c'est le relais qui transmet l'identité de l'utilisateur.

// Messages de repli, employés quand le serveur ne fournit pas le sien.
function errorMessage(status, body, defaut) {
    if (body && body.error && body.error.message) {
        return body.error.message
    }
    if (status === 401) {
        return "Session expirée ou invalide : merci de vous reconnecter."
    }
    if (status === 403) {
        return "Accès refusé."
    }
    if (status === 404) {
        return "Ce produit n'existe plus dans le catalogue."
    }
    return defaut + " (erreur " + status + ")."
}

// Le 403 sans corps (utilisateur non authentifié) n'est pas du JSON.
async function readBody(response) {
    try {
        return await response.json()
    } catch (parseError) {
        return {}
    }
}

/**
 * Charge le catalogue des produits canoniques adoptables.
 *
 * La réponse ne porte que l'identité des produits : ni prix, ni marge, ni
 * remise. Un compte qui parcourt le catalogue ne doit pas y lire la politique
 * tarifaire d'un autre.
 */
export function fetchCatalogue() {
    return async function (dispatch) {
        dispatch({
            type: "FETCH_CATALOGUE_START",
            payload: ""
        })

        try {
            const response = await fetch('/stocks/catalogue', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'same-origin',
            })

            const body = await readBody(response)

            if (!response.ok || (body && body.error)) {
                dispatch({
                    type: "FETCH_CATALOGUE_ERROR",
                    payload: errorMessage(response.status, body, "Impossible de charger le catalogue"),
                })
                return
            }

            dispatch({
                type: "FETCH_CATALOGUE_FULFILLED",
                payload: Array.isArray(body) ? body : [],
            })

        } catch (err) {
            console.log("[ERROR] fetchCatalogue ", err)
            dispatch({
                type: "FETCH_CATALOGUE_ERROR",
                payload: "Impossible de joindre le serveur.",
            })
        }
    }
}

// Relit la seule fiche adoptée et la pose dans le store.
//
// /stocks/single sert déjà les produits à l'unité et applique les réglages du
// compte au produit rendu : on obtient donc la fiche telle que le serveur la
// sert, valeurs commerciales comprises, et non un objet reconstitué ici.
//
// Rien de ce qui se passe ici ne doit toucher inventory.state.error. Cet état
// est lu par StockDashboard comme une panne globale : il remplace tout le
// module par « Magasin Indisponible », et seul un chargement réussi l'efface,
// or plus aucun écran n'est monté pour en déclencher un. Une relecture
// manquée ne coûte qu'un rafraîchissement de page ; l'adoption, elle, a
// réussi, et la présenter comme une panne serait à la fois faux et coûteux.
//
// @returns {boolean} vrai si le store porte désormais la fiche.
async function relireProduitAdopte(dispatch, productID) {
    try {
        const response = await fetch('/stocks/single', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                page: "products",
                id: productID,
            }),
            credentials: 'same-origin',
        })

        const produit = await readBody(response)

        if (!response.ok || !produit || produit.error || !produit.id) {
            console.log("[ERROR] relecture du produit adopté ", productID, produit)
            return false
        }

        // src est le cache local des photos, que formatDataProds pose sur
        // chaque fiche venant du serveur.
        produit.src = []

        dispatch(setProduct(produit))
        return true

    } catch (err) {
        console.log("[ERROR] relecture du produit adopté ", err)
        return false
    }
}

/**
 * Ajoute un produit du catalogue au compte connecté, en lui donnant un prix.
 *
 * @param {string} productID identifiant du produit canonique.
 * @param {object} prix prix_venteHT, prix_venteTTC, tva_rate, reduc_max.
 * @returns {object} product_id, l'identifiant réellement adopté tel que le
 *          serveur le renvoie — une fiche absorbée est redirigée vers sa
 *          canonique plutôt que refusée, il peut donc différer de celui
 *          envoyé — et rafraichi, faux quand la fiche n'a pas pu être relue et
 *          que l'écran devra le dire à l'utilisateur.
 *
 * Échoue en levant une erreur, comme deleteProduct : l'appel vient d'un geste
 * explicite de l'utilisateur, qui attend un message immédiat et non un état
 * d'erreur silencieux dans le store.
 */
export function adoptCatalogueProduct(productID, prix) {
    return async function (dispatch) {
        dispatch({
            type: "ADOPT_CATALOGUE_START",
            payload: productID
        })

        let body = {}
        let response = null

        try {
            response = await fetch('/stocks/catalogue/adopt', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    product_id: productID,
                    prix_venteHT: prix.prix_venteHT,
                    prix_venteTTC: prix.prix_venteTTC,
                    tva_rate: prix.tva_rate,
                    reduc_max: prix.reduc_max,
                }),
                credentials: 'same-origin',
            })

            body = await readBody(response)

        } catch (err) {
            console.log("[ERROR] adoptCatalogueProduct ", err)
            dispatch({
                type: "ADOPT_CATALOGUE_ERROR",
                payload: "Impossible de joindre le serveur.",
            })
            throw new Error("Impossible de joindre le serveur.")
        }

        if (!response.ok || (body && body.error)) {
            const message = errorMessage(response.status, body, "L'ajout du produit a échoué")
            dispatch({
                type: "ADOPT_CATALOGUE_ERROR",
                payload: message,
            })
            throw new Error(message)
        }

        const adopte = body.product_id || productID

        dispatch({
            type: "ADOPT_CATALOGUE_FULFILLED",
            payload: adopte,
        })

        // La liste des produits du compte est alimentée une seule fois, au
        // montage de l'écran Stock : sans cette relecture, le produit adopté
        // n'existerait dans le store qu'au prochain rechargement de la page.
        // On ne relit que lui, et jamais tout l'inventaire : adopter dix
        // produits d'affilée est l'usage visé, pas dix rechargements complets.
        const rafraichi = await relireProduitAdopte(dispatch, adopte)

        return { product_id: adopte, rafraichi }
    }
}

/**
 * Cherche, avant la création, un produit du catalogue global portant le même
 * libellé ou un libellé ressemblant.
 *
 * Ce n'est volontairement pas un thunk : la réponse ne sert qu'au formulaire
 * qui l'a demandée, le temps de la saisie, et n'a aucune raison de survivre
 * dans le store. Surtout, rien ici ne doit pouvoir poser un état d'erreur
 * global : une vérification qui échoue ne doit jamais empêcher de créer un
 * produit.
 *
 * @param {string} name libellé saisi.
 * @param {string} marque marque saisie, facultative : elle renforce le score
 *        sans être éliminatoire, un même article pouvant être saisi avec ou
 *        sans marque.
 * @returns {object|null} { name, identique, proches } où name est le libellé
 *          réellement vérifié, pour que l'appelant écarte une réponse devenue
 *          obsolète ; null quand la vérification n'a pas pu aboutir, et que
 *          l'écran ne doit donc rien affirmer.
 */
export async function checkProductDuplicate(name, marque) {
    const libelle = (name || "").trim()
    if (!libelle) {
        return null
    }

    try {
        const response = await fetch('/stocks/check-duplicate', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                name: libelle,
                marque: marque || "",
            }),
            credentials: 'same-origin',
        })

        const body = await readBody(response)

        if (!response.ok || (body && body.error)) {
            console.log("[ERROR] checkProductDuplicate ", response.status, body)
            return null
        }

        return {
            name: libelle,
            identique: (body && body.identique) || null,
            proches: (body && Array.isArray(body.proches)) ? body.proches : [],
        }

    } catch (err) {
        console.log("[ERROR] checkProductDuplicate ", err)
        return null
    }
}

/**
 * Demande au modèle son avis sur les candidats que la comparaison de caractères
 * n'a pas su trancher.
 *
 * Le lot 1 refuse un libellé identique et signale les ressemblants ; entre les
 * deux subsiste une zone qui n'est plus orthographique. « Batt. Li 12V 100 Ah »
 * partage peu de trigrammes avec « Batterie Lithium 12V 100Ah » et désigne le
 * même article ; « Panneau 100W mono » en partage beaucoup avec « Panneau 100W
 * poly » et désigne un autre article. Le modèle sait ces choses-là, un seuil
 * non.
 *
 * Comme checkProductDuplicate, ce n'est volontairement pas un thunk : aucun
 * dispatch, donc aucun moyen pour un avis qui échoue de poser un état d'erreur
 * global et de remplacer le module Stock par un écran indisponible. Un avis
 * n'est pas une vérification, il ne doit rien pouvoir empêcher.
 *
 * @param {string} name libellé saisi.
 * @param {string} marque marque saisie, facultative.
 * @param {Array} candidats les produits déjà retenus par le lot 1, objets ou
 *        identifiants. Seuls les identifiants partent : c'est le serveur qui
 *        recharge les libellés du catalogue, jamais le navigateur qui les lui
 *        dicte.
 * @returns {object|null} { name, verdict, produit, raison } où name est le
 *          libellé réellement soumis, pour que l'appelant écarte un avis devenu
 *          obsolète ; null quand il n'y a rien à dire — service indisponible,
 *          appel en échec, ou réponse illisible. L'écran n'affiche alors rien
 *          de plus.
 */
export async function checkProductDuplicateAI(name, marque, candidats) {
    const libelle = (name || "").trim()

    // Au plus dix candidats : le catalogue entier ne part pas au modèle, ni le
    // coût ni la latence ne le permettraient, et la question posée n'aurait
    // plus de sens.
    const ids = (candidats || [])
        .map( candidat => (candidat && candidat.id) ? candidat.id : candidat )
        .filter( id => typeof id === "string" && id !== "" )
        .slice(0, 10)

    if (!libelle || ids.length === 0) {
        return null
    }

    try {
        const response = await fetch('/stocks/check-duplicate/ai', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                name: libelle,
                marque: marque || "",
                candidats: ids,
            }),
            credentials: 'same-origin',
        })

        const body = await readBody(response)

        if (!response.ok || (body && body.error)) {
            console.log("[ERROR] checkProductDuplicateAI ", response.status, body)
            return null
        }

        const verdict = body && body.verdict

        // « indisponible », ou tout ce qu'on ne sait pas lire, se tait. Inventer
        // un avis à partir d'une réponse incomprise serait pire que ne rien
        // dire.
        if (verdict !== "doublon" && verdict !== "distinct") {
            return null
        }

        return {
            name: libelle,
            verdict: verdict,
            produit: (verdict === "doublon" && body.produit) ? body.produit : null,
            raison: (body && typeof body.raison === "string") ? body.raison.trim() : "",
        }

    } catch (err) {
        console.log("[ERROR] checkProductDuplicateAI ", err)
        return null
    }
}
